import { create } from 'zustand';
import { supabase } from '../api/supabase';

interface DisplaySettings {
  // Vehicle Card Settings
  showVehicleRentalRates: boolean;
  showVehicleVIN: boolean;
  showVehicleOdometer: boolean;
  showVehicleImages: boolean;
  vehicleCompactMode: boolean;
  
  // Driver Card Settings
  showDriverRates: boolean;
  showDriverBalance: boolean;
  showDriverPhone: boolean;
  showDriverEmiratesId: boolean;
  showDriverPhotos: boolean;
  driverCompactMode: boolean;
  
  // General
  loading: boolean;
  error: string | null;
}

interface DisplaySettingsStore extends DisplaySettings {
  loadSettings: () => Promise<void>;
  updateSetting: (key: keyof DisplaySettings, value: boolean) => Promise<void>;
  resetToDefaults: () => Promise<void>;
}

const DEFAULT_SETTINGS: DisplaySettings = {
  // Vehicle defaults - show everything
  showVehicleRentalRates: true,
  showVehicleVIN: true,
  showVehicleOdometer: true,
  showVehicleImages: true,
  vehicleCompactMode: false,
  
  // Driver defaults - show everything
  showDriverRates: true,
  showDriverBalance: true,
  showDriverPhone: true,
  showDriverEmiratesId: true,
  showDriverPhotos: true,
  driverCompactMode: false,
  
  loading: false,
  error: null,
};

export const useDisplaySettingsStore = create<DisplaySettingsStore>((set, get) => ({
  ...DEFAULT_SETTINGS,

  loadSettings: async () => {
    set({ loading: true, error: null });
    
    try {
      // Fetch all display settings from system_settings table
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .like('setting_key', 'display_%');

      if (error) throw error;

      // Convert database settings to store state
      const settings: Partial<DisplaySettings> = {};
      
      if (data) {
        data.forEach((setting) => {
          const key = setting.setting_key.replace('display_', '');
          const camelCaseKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
          settings[camelCaseKey as keyof DisplaySettings] = setting.setting_value === 'true';
        });
      }

      set({ ...settings, loading: false });
    } catch (err) {
      console.error('Error loading display settings:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to load settings', loading: false });
    }
  },

  updateSetting: async (key: keyof DisplaySettings, value: boolean) => {
    try {
      // Convert camelCase to snake_case for database
      const dbKey = `display_${key.replace(/([A-Z])/g, '_$1').toLowerCase()}`;

      // Upsert setting in database
      const { error } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: dbKey,
          setting_value: value.toString(),
          setting_type: 'boolean',
          description: `Display setting for ${key}`,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'setting_key'
        });

      if (error) throw error;

      // Update local state
      set({ [key]: value });
      
      console.log(`✅ Updated ${key} to ${value}`);
    } catch (err) {
      console.error(`Error updating setting ${key}:`, err);
      set({ error: err instanceof Error ? err.message : 'Failed to update setting' });
    }
  },

  resetToDefaults: async () => {
    try {
      set({ loading: true });
      
      // Delete all display settings from database
      const { error } = await supabase
        .from('system_settings')
        .delete()
        .like('setting_key', 'display_%');

      if (error) throw error;

      // Reset to defaults
      set({ ...DEFAULT_SETTINGS, loading: false });
      
      console.log('✅ Reset to default display settings');
    } catch (err) {
      console.error('Error resetting settings:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to reset settings', loading: false });
    }
  },
}));
